import type { Map as MapboxMap } from 'mapbox-gl';

/** Tracks which SVG URL was last registered per icon id (survives sprite pre-load). */
const loadedSvgById = new Map<string, string>();
/** Ids we registered with addImage (fallback) — safe to removeImage on teardown. */
const runtimeAddedIds = new Set<string>();
const inFlightById = new Map<string, Promise<void>>();

const MAP_ICON_PIXEL_RATIO = 2;

export function clearCoordalSvgIconCache(): void {
  loadedSvgById.clear();
  runtimeAddedIds.clear();
  inFlightById.clear();
}

/**
 * Mapbox GL cannot addImage() raw SVG — rasterize to ImageData first.
 * Bitmap size must match the style sprite (48×48 POI, 60×60 landmark @ pixelRatio 2).
 * Do not scale the canvas by pixelRatio here — Mapbox applies pixelRatio on addImage().
 */
async function loadSvgAsImageData(url: string): Promise<ImageData> {
  const img = new Image();
  img.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load Coordal icon: ${url}`));
    img.src = url;
  });

  const baseW = img.naturalWidth || img.width;
  const baseH = img.naturalHeight || img.height;
  if (!baseW || !baseH) {
    throw new Error(`Coordal icon has no dimensions: ${url}`);
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(baseW));
  canvas.height = Math.max(1, Math.round(baseH));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable for icon rasterize');

  ctx.drawImage(img, 0, 0, baseW, baseH);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

function registerCoordalImage(map: MapboxMap, id: string, imageData: ImageData): void {
  if (map.hasImage(id)) {
    try {
      map.updateImage(id, imageData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('width and height')) {
        // Pre-loaded from style sprite at fixed dimensions — keep sprite bitmap.
        return;
      }
      throw err;
    }
    return;
  }
  try {
    map.addImage(id, imageData, { pixelRatio: MAP_ICON_PIXEL_RATIO });
    runtimeAddedIds.add(id);
  } catch (e) {
    if (map.hasImage(id)) {
      try {
        map.updateImage(id, imageData);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes('width and height')) return;
        throw err;
      }
      return;
    }
    throw e;
  }
}

/** Load Coordal SVG badges into the map sprite store (rasterized). */
export function loadCoordalSvgIcon(map: MapboxMap, id: string, url: string): Promise<void> {
  if (loadedSvgById.get(id) === url && map.hasImage(id)) return Promise.resolve();

  // Style sprite already registered this id (maki @2x) — no runtime swap needed.
  if (map.hasImage(id) && !runtimeAddedIds.has(id)) {
    loadedSvgById.set(id, url);
    return Promise.resolve();
  }

  const inFlight = inFlightById.get(id);
  if (inFlight) return inFlight;

  const promise = loadSvgAsImageData(url)
    .then((imageData) => {
      registerCoordalImage(map, id, imageData);
      loadedSvgById.set(id, url);
    })
    .finally(() => {
      if (inFlightById.get(id) === promise) inFlightById.delete(id);
    });

  inFlightById.set(id, promise);
  return promise;
}

export async function ensureCoordalSvgIcons(
  map: MapboxMap,
  basePath: string,
  icons: ReadonlyArray<{ id: string; file: string }>,
): Promise<void> {
  if (!map.isStyleLoaded()) return;

  const base = `${window.location.origin}${basePath}`;
  const results = await Promise.allSettled(
    icons.map((icon) => loadCoordalSvgIcon(map, icon.id, `${base}/${icon.file}`)),
  );
  for (let i = 0; i < results.length; i++) {
    if (results[i].status === 'rejected') {
      const icon = icons[i];
      console.warn(`[coordal-icons] failed to load ${icon.id}:`, (results[i] as PromiseRejectedResult).reason);
    }
  }
}

export function removeCoordalSvgIcons(map: MapboxMap, ids: readonly string[]) {
  for (const id of ids) {
    if (!runtimeAddedIds.has(id)) continue;
    try {
      if (map.hasImage(id)) map.removeImage(id);
    } catch {
      /* style swap race */
    }
    runtimeAddedIds.delete(id);
    loadedSvgById.delete(id);
  }
}
